<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

// Multi-line sibling of FormInput: same label, surface, horizontal padding and
// type scale, but with a real line-height and vertical padding (a textarea
// styled like a one-line input puts the first line against the top border).
// It grows with its content from `rows` up to `maxRows`, then scrolls; the
// resize handle still lets the user drag it taller.
const props = defineProps({
	id: {
		type: String,
		default: undefined,
	},
	label: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	/** Muted helper line under the field. */
	hint: {
		type: String,
		default: '',
	},
	maxlength: {
		type: Number,
		default: 524288,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	readonly: {
		type: Boolean,
		default: false,
	},
	isError: {
		type: Boolean,
		default: false,
	},
	/** Minimum visible rows. */
	rows: {
		type: Number,
		default: 3,
	},
	/** Rows the field grows to before it scrolls. */
	maxRows: {
		type: Number,
		default: 8,
	},
	textareaClasses: {
		type: String,
		default: '',
	},
	modelValue: {
		type: [String, Number],
		default: '',
	},
})

const emit = defineEmits(['update:modelValue', 'focus', 'blur'])

const el = ref(null)
let resizeObserver = null
let lastWidth = 0

const value = computed({
	get: () => (props.modelValue == null ? '' : String(props.modelValue)),
	set: (v) => emit('update:modelValue', v),
})

const textareaClass = computed(() => {
	const base = [
		'block w-full resize-y rounded bg-surface-3 px-3 py-2.5 text-base leading-normal text-surface-text-2 transition-colors',
		'placeholder:text-surface-6 focus:bg-surface-4 focus:outline-0 focus:ring-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-surface-7 md:text-tiny',
	]
	base.push(props.isError
		? 'ring-1 ring-error-light focus:ring-error-light bg-error-transparent'
		: 'border-neutral-n50 focus:border-surfaces-primary')
	if (props.textareaClasses) base.push(props.textareaClasses)
	return base
})

/** Lift the min-height to the content (between `rows` and `maxRows`) so the field grows as the user types. */
function fit() {
	const ta = el.value
	if (!ta) return
	const cs = getComputedStyle(ta)
	const line = parseFloat(cs.lineHeight) || 20
	const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
	const border = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth)
	const min = Math.ceil(props.rows * line + pad + border)
	const max = Math.ceil(Math.max(props.maxRows, props.rows) * line + pad + border)
	ta.style.minHeight = `${min}px`
	if (!ta.getClientRects().length) return // hidden (v-show / closed modal): measure once it is visible
	const content = ta.scrollHeight + border
	ta.style.minHeight = `${Math.min(Math.max(content, min), max)}px`
	ta.style.overflowY = content > max ? 'auto' : 'hidden'
}

watch(() => props.modelValue, () => nextTick(fit))
watch(() => [props.rows, props.maxRows], () => nextTick(fit))

onMounted(() => {
	fit()
	if (typeof ResizeObserver !== 'undefined' && el.value) {
		// re-measure when the field becomes visible or changes width (text re-wraps), not on our own height changes
		resizeObserver = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect?.width || 0
			if (width !== lastWidth) {
				lastWidth = width
				fit()
			}
		})
		resizeObserver.observe(el.value)
	}
})
onBeforeUnmount(() => resizeObserver?.disconnect())

defineExpose({
	focus: () => el.value?.focus(),
	blur: () => el.value?.blur(),
	fit,
})
</script>

<template>
	<div class="w-full min-w-0">
		<label v-if="label" class="mb-2 block font-medium text-tiny text-surface-text-1" :for="id">{{ label }}</label>
		<textarea
			:id="id"
			ref="el"
			v-model="value"
			:class="textareaClass"
			:rows="rows"
			:placeholder="placeholder"
			:maxlength="maxlength"
			:disabled="disabled"
			:readonly="readonly"
			:aria-invalid="isError || undefined"
			@input="fit"
			@focus="emit('focus', $event)"
			@blur="emit('blur', $event)"
		/>
		<p v-if="hint" class="mb-0 mt-1.5 text-tiny" :class="isError ? 'text-error' : 'text-surface-7'">{{ hint }}</p>
	</div>
</template>
